import { useRef, useState } from 'react';
import {
  Announcements,
  MouseSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

export function useSortableList<TItem extends { id: string }>({
  items,
}: {
  items: TItem[];
}) {
  const isFirstAnnouncement = useRef(true);
  const getIndex = (id: UniqueIdentifier) =>
    items.findIndex((item) => item.id === id);
  const getPosition = (id: UniqueIdentifier) => getIndex(id) + 1;
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const activeIndex = activeId ? getIndex(activeId) : -1;
  const mouseSensor = useSensor(MouseSensor, {
    // Require the mouse to move by 10 pixels before activating
    activationConstraint: {
      distance: 10,
    },
  });
  const sensors = useSensors(mouseSensor);

  const announcements: Announcements = {
    onDragStart({ active }) {
      return `Picked up sortable item ${String(
        active.data.current?.name,
      )}. Sortable item ${active.data.current
        ?.name} is in position ${getPosition(active.id)} of ${items.length}`;
    },
    onDragOver({ active, over }) {
      // In this specific use-case, the picked up item's `id` is always the same as the first `over` id.
      // The first `onDragOver` event therefore doesn't need to be announced, because it is called
      // immediately after the `onDragStart` announcement and is redundant.
      if (isFirstAnnouncement.current === true) {
        isFirstAnnouncement.current = false;
        return;
      }

      if (over) {
        return `Sortable item ${
          active.id
        } was moved into position ${getPosition(over.id)} of ${items.length}`;
      }

      return;
    },
    onDragEnd({ active, over }) {
      if (over) {
        return `Sortable item was dropped at position ${getPosition(
          over.id,
        )} of ${items.length}`;
      }

      return;
    },
    onDragCancel({ active }) {
      return `Sorting was cancelled. Sortable item was dropped and returned to position ${getPosition(
        active.id,
      )} of ${items.length}.`;
    },
  };

  return {
    activeId,
    setActiveId,
    activeIndex,
    getIndex,
    getPosition,
    announcements,
    sensors,
  };
}
